import { useToast } from '@/features/common/components/Toast/contexts/Provider'
import { OpenTabState, PaymentType } from '@/features/graphql/generated/graphql'
import useAccountRecords from '@/features/openTabs/contexts/OpenTabsData/hooks/useAccountRecords'
import useCatalogDraft from '@/features/openTabs/contexts/OpenTabsData/hooks/useCatalogDraft'
import { logger } from '@/features/openTabs/logger'
import { OpenTabSettleDocument } from '@/features/openTabs/mutations'
import {
    AccountPaymentMode,
    AccountPaymentSelection,
    CommitAccountPaymentInput,
    OpenAccountSettlementValue,
} from '@/features/openTabs/types'
import {
    getAccountPaymentTotal,
    isFullAccountSelection,
    isValidAccountSelection,
} from '@/features/openTabs/utils/accountCalculations'
import { toAccountDetail } from '@/features/openTabs/utils/backendTransforms'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useMutation } from '@apollo/client'
import { RefObject, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface UseAccountSettlementArgs {
    records: ReturnType<typeof useAccountRecords>
    catalog: ReturnType<typeof useCatalogDraft>
    selection: AccountPaymentSelection
    selectionMode: AccountPaymentMode
    clearSelection: () => void
    settleAccountInFlightRef: RefObject<boolean>
}

const useAccountSettlement = ({
    records,
    catalog,
    selection: paymentSelection,
    selectionMode: paymentSelectionMode,
    clearSelection: clearPaymentSelection,
    settleAccountInFlightRef,
}: UseAccountSettlementArgs): Pick<
    OpenAccountSettlementValue,
    'settlingAccount' | 'settleAccount'
> => {
    const { t } = useTranslation('app')
    const { show: showToast } = useToast()
    const handleError = useHandleErrorWithToast(logger)
    const [settlingAccount, setSettlingAccount] = useState(false)
    const [settleOpenTab] = useMutation(OpenTabSettleDocument)
    const {
        value: {
            selectedAccount,
            paymentMethods,
            detailLoading,
            detailFailed,
            detailRefreshFailed,
            listFailed,
        },
        detailAccountId,
        refetchSummaries,
        refetchDetail,
        setSelectedAccountId,
        setActiveStatusState,
    } = records
    const {
        value: { clearCatalogCart },
        catalogMutationInFlightRef,
        catalogDraftVersionRef,
    } = catalog

    const settleAccount = useCallback(
        async (accountId: string, input: CommitAccountPaymentInput): Promise<boolean> => {
            if (
                detailLoading ||
                detailFailed ||
                detailRefreshFailed ||
                listFailed ||
                settlingAccount ||
                settleAccountInFlightRef.current ||
                catalogMutationInFlightRef.current ||
                selectedAccount?.id !== accountId ||
                detailAccountId !== accountId ||
                !isValidAccountSelection(selectedAccount, paymentSelection) ||
                (paymentSelectionMode === 'all' &&
                    !isFullAccountSelection(selectedAccount, paymentSelection)) ||
                !paymentMethods.includes(input.method) ||
                (input.method === PaymentType.Cash &&
                    (input.receivedAmount === undefined ||
                        !Number.isFinite(input.receivedAmount) ||
                        input.receivedAmount <
                            getAccountPaymentTotal(selectedAccount, paymentSelection)))
            ) {
                return false
            }

            settleAccountInFlightRef.current = true
            setSettlingAccount(true)
            const draftVersion = catalogDraftVersionRef.current
            const selectedItems = selectedAccount.orders
                .flatMap((order) => order.items)
                .filter((item) => paymentSelection[item.id] !== undefined)
            const fullSelection =
                paymentSelectionMode === 'all' ||
                isFullAccountSelection(selectedAccount, paymentSelection)
            let mutationAttempted = false
            try {
                const refreshed = await refetchDetail()
                if (draftVersion !== catalogDraftVersionRef.current) return false

                const refreshedAccount =
                    refreshed?.uuid === accountId
                        ? toAccountDetail(refreshed, selectedAccount.number)
                        : undefined
                const refreshedItems = new Map(
                    refreshedAccount?.orders.flatMap((order) =>
                        order.items.map((item) => [item.id, item] as const)
                    )
                )
                if (
                    !refreshedAccount ||
                    !isValidAccountSelection(refreshedAccount, paymentSelection) ||
                    (fullSelection &&
                        !isFullAccountSelection(refreshedAccount, paymentSelection)) ||
                    refreshedAccount.currency !== selectedAccount.currency ||
                    selectedItems.some((item) => {
                        const refreshedItem = refreshedItems.get(item.id)

                        return (
                            !refreshedItem ||
                            refreshedItem.quantity !== item.quantity ||
                            refreshedItem.unitPrice !== item.unitPrice ||
                            refreshedItem.totalAmount !== item.totalAmount ||
                            refreshedItem.currency !== item.currency
                        )
                    })
                ) {
                    clearPaymentSelection()
                    showToast(
                        t('openTabs.feedback.accountChangedTitle'),
                        t('openTabs.feedback.accountChangedMessage')
                    )
                    void refetchSummaries().catch(() => undefined)
                    return false
                }

                mutationAttempted = true
                const result = await settleOpenTab({
                    variables: {
                        openTabUuid: accountId,
                        input: {
                            paymentType: input.method,
                            items: selectedItems.map(({ id: itemUuid }) => ({
                                itemUuid,
                                quantity: paymentSelection[itemUuid],
                            })),
                        },
                    },
                })

                if (!result.data?.openTabSettle.uuid) {
                    throw new Error(t('openTabs.payment.failed'))
                }

                const settledTab = await refetchDetail().catch(() => undefined)

                if (draftVersion === catalogDraftVersionRef.current) {
                    clearPaymentSelection()
                    clearCatalogCart()
                    if (
                        settledTab?.state === OpenTabState.Closed ||
                        (!settledTab && isFullAccountSelection(selectedAccount, paymentSelection))
                    ) {
                        setSelectedAccountId(null)
                        setActiveStatusState('open')
                    }
                }
                void refetchSummaries().catch(() => undefined)

                return true
            } catch (error) {
                if (mutationAttempted) void handleError(error)
                if (draftVersion === catalogDraftVersionRef.current) clearPaymentSelection()

                // A lost response may still mean the server accepted the payment.
                // Refresh the remaining quantities before allowing another selection.
                if (mutationAttempted)
                    await Promise.allSettled([refetchDetail(), refetchSummaries()])
                return false
            } finally {
                settleAccountInFlightRef.current = false
                setSettlingAccount(false)
            }
        },
        [
            catalogDraftVersionRef,
            catalogMutationInFlightRef,
            clearCatalogCart,
            clearPaymentSelection,
            detailAccountId,
            detailLoading,
            detailFailed,
            detailRefreshFailed,
            listFailed,
            handleError,
            refetchSummaries,
            refetchDetail,
            selectedAccount,
            setActiveStatusState,
            setSelectedAccountId,
            settleAccountInFlightRef,
            settleOpenTab,
            paymentMethods,
            settlingAccount,
            showToast,
            paymentSelection,
            paymentSelectionMode,
            t,
        ]
    )

    return useMemo(() => ({ settlingAccount, settleAccount }), [settlingAccount, settleAccount])
}

export default useAccountSettlement
